import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useT } from '@hlt/web-kit/i18n';
import { Button, UnstyledButton } from '@hlt/web-kit/ui';

/**
 * The editor's steps, in order. Every step can be opened at any time.
 */
const editorSteps = ['areas', 'questions', 'preview'] as const;
export type EditorStep = (typeof editorSteps)[number];

export const isEditorStep = (value: unknown): value is EditorStep =>
    typeof value === 'string' && (editorSteps as readonly string[]).includes(value);

const labels: Record<EditorStep, { short: string; full: string }> = {
    areas: { short: 'templates.editor.stepAreasShort', full: 'templates.editor.stepAreas' },
    questions: { short: 'templates.editor.stepQuestionsShort', full: 'templates.editor.stepQuestions' },
    preview: { short: 'templates.editor.stepPreviewShort', full: 'templates.editor.stepPreview' },
};

/** Numbered step strip under the title; a dot marks steps with something to fix before publishing. */
export function EditorSteps({
    current,
    attention,
    onSelect,
}: {
    current: EditorStep;
    attention: ReadonlySet<EditorStep>;
    onSelect: (step: EditorStep) => void;
}) {
    const t = useT();
    return (
        <nav aria-label={t('templates.editor.steps')}>
            <ol className="hlt-editor-steps">
                {editorSteps.map((step, i) => (
                    <li key={step}>
                        <UnstyledButton
                            type="button"
                            className="hlt-editor-step"
                            aria-current={step === current ? 'step' : undefined}
                            data-state={step === current ? 'current' : 'todo'}
                            onClick={() => onSelect(step)}
                        >
                            <span className="hlt-step-dot" aria-hidden>
                                {i + 1}
                            </span>
                            <span className="hlt-editor-step-label">
                                <span aria-hidden>{t(labels[step].short)}</span>
                                <span className="sr-only">
                                    {t('templates.editor.stepNumber', {
                                        number: i + 1,
                                        total: editorSteps.length,
                                        name: t(labels[step].full),
                                    })}
                                    {attention.has(step) ? ` (${t('templates.outline.hasIssues')})` : ''}
                                </span>
                            </span>
                            {attention.has(step) ? <span className="hlt-editor-step-flag" aria-hidden /> : null}
                        </UnstyledButton>
                    </li>
                ))}
            </ol>
        </nav>
    );
}

export function StepFooter({ current, onSelect }: { current: EditorStep; onSelect: (step: EditorStep) => void }) {
    const t = useT();
    const index = editorSteps.indexOf(current);
    const previous = editorSteps[index - 1];
    const next = editorSteps[index + 1];
    return (
        <nav
            aria-label={t('templates.editor.stepNavigation')}
            className="flex items-center justify-between gap-3 border-t border-border pt-4"
        >
            {previous ? (
                <Button
                    variant="default"
                    leftSection={<ChevronLeft aria-hidden className="size-4" />}
                    onClick={() => onSelect(previous)}
                >
                    {t('common.back')}
                </Button>
            ) : (
                <span />
            )}
            {next ? (
                <Button
                    variant="default"
                    rightSection={<ChevronRight aria-hidden className="size-4" />}
                    onClick={() => onSelect(next)}
                    // The shared button style keeps buttons from shrinking; this long label truncates on phones instead.
                    className="min-w-0"
                    style={{ flexShrink: 1 }}
                >
                    <span className="truncate">{t('templates.editor.next', { step: t(labels[next].full) })}</span>
                </Button>
            ) : null}
        </nav>
    );
}
